import { useCallback, useEffect, useState } from "react";
import { generateExamQuestions } from "./quizUtils";
import QuizQuestion from "./QuizQuestion";
import styles from "./Quiz.module.css";

const EXAM_LENGTH = 20;

function formatTime(ms) {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function ExamMode({ poses, language = "russian", progress = {}, onAnswer, rootItems = [] }) {
  const [stage, setStage] = useState("start");
  const [questions, setQuestions] = useState([]);
  const [qIndex, setQIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [answered, setAnswered] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [finishedAt, setFinishedAt] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (stage !== "running") return undefined;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [stage]);

  const start = useCallback(() => {
    setQuestions(generateExamQuestions(poses, Math.min(EXAM_LENGTH, poses.length), language, progress, rootItems));
    setQIndex(0);
    setScore(0);
    setAnswered(false);
    setStartedAt(Date.now());
    setFinishedAt(null);
    setStage("running");
  }, [poses, language, progress, rootItems]);

  const handleAnswered = (isCorrect) => {
    setAnswered(true);
    if (isCorrect) setScore((s) => s + 1);
    onAnswer?.(questions[qIndex].pose, isCorrect);
  };

  const handleNext = () => {
    if (qIndex + 1 >= questions.length) {
      setFinishedAt(Date.now());
      setStage("finished");
      return;
    }
    setQIndex((i) => i + 1);
    setAnswered(false);
  };

  if (poses.length < 4) {
    return <p className={styles.emptyNote}>Недостаточно поз для экзамена.</p>;
  }

  if (stage === "start") {
    return (
      <div className={styles.examStartCard}>
        <p className={styles.examStartTitle}>
          {Math.min(EXAM_LENGTH, poses.length)} случайных вопросов на время
        </p>
        <p className={styles.examStartSub}>название, картинка и корни</p>
        <button type="button" className={styles.nextBtn} onClick={start}>
          Начать экзамен
        </button>
      </div>
    );
  }

  if (stage === "finished") {
    const pct = questions.length ? Math.round((score / questions.length) * 100) : 0;
    const elapsed = finishedAt - startedAt;
    return (
      <div className={styles.examResultCard}>
        <p className={styles.examResultTitle}>Результат</p>
        <div className={styles.examResultPct}>{pct}%</div>
        <p className={styles.examResultLine}>
          Правильно: {score} из {questions.length}
        </p>
        <p className={styles.examResultLine}>Время: {formatTime(elapsed)}</p>
        <button type="button" className={styles.nextBtn} onClick={start}>
          Пройти ещё раз
        </button>
      </div>
    );
  }

  const question = questions[qIndex];
  const elapsed = now - startedAt;

  return (
    <div className={styles.quizWrap}>
      <div className={styles.examHeaderRow}>
        <span>
          {qIndex + 1} / {questions.length}
        </span>
        <span>{formatTime(elapsed)}</span>
      </div>
      <div className={styles.progressBar}>
        <div
          className={styles.progressFill}
          style={{ width: `${((qIndex + (answered ? 1 : 0)) / questions.length) * 100}%` }}
        />
      </div>

      {question && (
        <QuizQuestion
          key={qIndex}
          question={question}
          onAnswered={handleAnswered}
          rootItems={rootItems}
          language={language}
        />
      )}

      <button type="button" className={styles.nextBtn} onClick={handleNext} disabled={!answered}>
        {qIndex + 1 >= questions.length ? "Завершить" : "Далее"}
      </button>
    </div>
  );
}
